import { Component } from 'san';
import * as _ from 'lodash';

import {
  IAudit,
  IHandlerPerson,
  IProject,
  IWorkflowStep,
  IWorkflowStepAddData
} from 'src/models';
import { EWorkflowStepType, EWorkflowType } from 'src/enums';
import { WORKFLOW_STEP_START_RANK } from 'src/consts';

import WorkflowStep from '../WorkflowStep/WorkflowStep';
import WorkflowStepTip from '../WorkflowStepTip/WorkflowStepTip';

import auditService from 'src/services/audit.service';
import workflowService from 'src/services/workflow.service';

export default class Workflow extends Component {
  constructor(options: any) {
    super(options);
  }

  static components = {
    'ui-workflow-step': WorkflowStep,
    'ui-workflow-step-tip': WorkflowStepTip
  }

  static computed = {
    startStep() {
      const steps = this.data.get('steps');
      return _.find(steps, { type: EWorkflowStepType.Start });
    },
    normalSteps() {
      const steps = this.data.get('steps');
      return _.filter(steps, { type: EWorkflowStepType.Normal });
    },
    endStep() {
      const steps = this.data.get('steps');
      return _.find(steps, { type: EWorkflowStepType.End });
    },
    // 驳回到节点索引
    rejectToStep() {
      // 选中的节点是当前节点之前的？驳回到选中的节点：驳回到开始节点
      const steps = this.data.get('steps');
      const checkedStepRank = _.findIndex(steps, { _is_checked: true });
      return -1 === checkedStepRank ? WORKFLOW_STEP_START_RANK : checkedStepRank;
    }
  }

  initData(): {
    isDirty: boolean;
    type: EWorkflowType;    // {{}}
    templateCode: string;
    auditId: string;    // {{}}
    editEnable: boolean;    // {{}} init传入editEnable，则取editEnable，无值则取 !audit.is_deleted && !audit.actions.is_detail
    isNew: boolean;
    steps: IWorkflowStep[];
    current: number;    // 当前节点的rank
    audit: IAudit;
    isShowTips: boolean;
    tips: string;
    defaultScore: number;
    scoreOptions: number[];
  } {
    return {
      isDirty: false,
      type: EWorkflowType.Issue,
      auditId: null,
      editEnable: undefined,
      isNew: false,
      steps: [],
      current: 0,
      audit: undefined,
      templateCode: undefined,
      isShowTips: false,
      tips: '',
      defaultScore: 0,
      scoreOptions: []
    };
  }

  inited() {
    const auditId = this.data.get('auditId');
    this.init(auditId);
  }

  //#region owner调用
  // 获取数据
  getDataSync(): {
    code: string;
    steps: IWorkflowStep[];
    rejectToStep?: number;
    currentStepPersons?: IHandlerPerson[];  // 当前节点的审批人
    tips: string;
    defaultScore: number;
    scoreOptions: number[];
  } {
    const startStep = this.data.get('startStep');
    const normalSteps = this.data.get('normalSteps');
    const endStep = this.data.get('endStep');
    const current = this.data.get('current');
    const currentStep: IWorkflowStep = _.find(normalSteps, { rank: current });
    return {
      code: this.data.get('templateCode'),
      steps: [startStep, ...normalSteps, endStep],
      rejectToStep: this.data.get('rejectToStep'),
      currentStepPersons: currentStep && currentStep.persons || [],
      tips: this.data.get('tips'),
      defaultScore: this.data.get('defaultScore'),
      scoreOptions: this.data.get('scoreOptions')
    }
  }
  // 合法性验证
  verifySync(): boolean {
    const startStep = this.data.get('startStep');
    const normalSteps = this.data.get('normalSteps');
    const endStep = this.data.get('endStep');
    return workflowService.verifyWorkflowSync([startStep, ...normalSteps, endStep])
  }
  // 暂存的合法性验证
  verifySaveSync(): boolean {
    const startStep = this.data.get('startStep');
    const normalSteps = this.data.get('normalSteps');
    const endStep = this.data.get('endStep');
    return workflowService.verifySaveWorkflowSync([startStep, ...normalSteps, endStep])
  }
  // 是否变更过
  getIsDirtySync(): boolean {
    return this.data.get('isDirty');
  }
  // 重置是否变更过
  resetIsDirtySync() {
    this.data.set('isDirty', false);
  }
  // 给工作流节点带出默认审批人
  assignWorkflowStepHandlerPersons(projectsGroup: {
    updatedProjects: IProject[];  // 本次变化的项目
    allDirtyProjects: IProject[]; // 所有变化的项目
  }) {
    const type = this.data.get('type');
    const current = this.data.get('current');
    const oldSteps = this.data.get('steps');
    workflowService.assignWorkflowStepHandlerPersons({
      workflow: { type, current, steps: oldSteps },
      projectsGroup,
      audit: this.data.get('audit')
    }).then(workflow => {
      const steps = workflow.steps;
      this.data.splice('steps', [0, oldSteps.length, ...steps]);
    });
  }
  // 更换工作流（用于：已审回执，更换工作流）
  changeWorkflow(workflowType: EWorkflowType) {
    const auditId = this.data.get('auditId');
    const oldSteps = this.data.get('steps');
    const audit = this.data.get('audit');
    auditService.getAuditProjects(auditId).then(projects => {
      return workflowService.generateWorkflowFromTemplate(workflowType, projects, audit);
    }).then(workflow => {
      const steps = workflow.steps;
      this.data.set('templateCode', workflow.code);
      this.data.splice('steps', [0, oldSteps.length, ...steps]);
      this.data.set('current', workflow.current);
      this.data.set('tips', workflow.tips);
      this.data.set('defaultScore', workflow.default_score);
      this.data.set('scoreOptions', workflow.score_options);
    });
  }
  //#endregion

  init(auditId: string) {
    const type = this.data.get('type');
    const steps = this.data.get('steps');
    auditService.getAudit(auditId).then(audit => {
      this.data.set('audit', audit);
    });
    auditService.getAuditWorkflowInfo(auditId, type).then(workflowInfo => {
      this.data.set('templateCode', workflowInfo.workflow.code);
      this.data.splice('steps', [0, steps.length, ...workflowInfo.workflow.steps]);
      this.data.set('current', workflowInfo.workflow.current);
      this.data.set('tips', workflowInfo.workflow.tips);
      this.data.set('defaultScore', workflowInfo.workflow.default_score);
      this.data.set('scoreOptions', workflowInfo.workflow.score_options);

      const dataKey = 'editEnable';
      const editEnable = this.data.get(dataKey);
      // init传入editEnable，则取editEnable，无值则取 !audit.is_deleted && !audit.actions.is_detail
      if (_.isUndefined(editEnable)) {
        this.data.set(dataKey, workflowInfo.edit_enable);
      }
    });
  }

  checkStep(step: IWorkflowStep) {
    const steps = this.data.get('steps');
    _.forEach(steps, (value, index) => {
      const dataKey = `steps[${index}]._is_checked`;
      if (value.rank === step.rank) {
        this.data.set(dataKey, !this.data.get(dataKey));
      } else {
        this.data.set(dataKey, false);
      }
    });
  }

  addStep(addData: IWorkflowStepAddData) {
    const dataKey = 'steps';
    const oldSteps = this.data.get(dataKey);
    const steps = workflowService.addWorkflowStepSync(addData, oldSteps);
    this.data.splice(dataKey, [0, oldSteps.length, ...steps]);
    this.data.set('isDirty', true);
  }

  deleteStep(step: IWorkflowStep) {
    const dataKey = 'steps';
    const oldSteps = this.data.get(dataKey);
    const steps = workflowService.deleteWorkflowStepSync(step, oldSteps);
    this.data.splice(dataKey, [0, oldSteps.length, ...steps]);
    this.data.set('isDirty', true);
  }

  updateStep(step: IWorkflowStep) {
    const dataKey = 'steps';
    const oldSteps = this.data.get(dataKey);
    const steps = workflowService.updateWorkflowStepSync(step, oldSteps);
    this.data.splice(dataKey, [0, oldSteps.length, ...steps]);
    this.data.set('isDirty', true);
  }

  showTips() {
    this.data.set('isShowTips', true);
  }

  hideTips() {
    this.data.set('isShowTips', false);
  }
}